import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';
import classnames from 'classnames';
import get from 'lodash.get';
import { getRoomViewItem } from './heroCarouselHelpers';
import { getSortedPhotos } from '../helpers/getSortedPhotos';
import { getThumbsSrcSetString } from '../../utils/srcSet';

// components
import { Carousel } from 'dibs-carousel';
import { FormattedMessage } from 'react-intl';
import LazyLoad from 'react-lazy-load';
import { PdpRespZoomModalLoader } from '../PdpRespZoomModal/PdpRespZoomModalLoader';
import { PdpRespHeroCarouselRequestMoreImages } from './PdpRespHeroCarouselRequestMoreImages';
import { PdpRespHeroCarouselViewInRoom } from './PdpRespHeroCarouselViewInRoom';
import { PdpRespHeroCarouselVimeoLazyLoader } from './PdpRespHeroCarouselVimeoLazyLoader';
import { PdpSharedHeroCarouselThumb } from './PdpSharedHeroCarouselThumb';
import { PdpSharedCarouselDot } from '../PdpSharedCarouselDot/PdpSharedCarouselDot';
import { SharedNotificationsWrapper } from '../../shared/SharedNotificationsWrapper';
import { PdpRespHeroCarouselTradeOnApprovalCalloutLazy } from './PdpRespHeroCarouselTradeOnApproval/PdpRespHeroCarouselTradeOnApprovalCalloutLazy';

// helpers
import {
    trackClickCarousel,
    trackChangeCarousel,
    trackViewRoomDisplayed,
    trackViewRoomClick,
} from './heroCarouselTracking';
import videoTracking from './videoTracking';

// constants
import { HORIZONTAL_OFFSET_RESP_IMG } from '../../constants/imageConstants';
import { NOTIFICATION_KEYS as SHARED_NOTIFICATION_KEYS } from '../PdpSharedNotifications/pdpSharedNotificationConstants';

// styles
import styles from './PdpRespHeroCarousel.scss';

// messages
const counter = (index, totalItems, videoPdpEnabled) =>
    videoPdpEnabled ? (
        <FormattedMessage
            id="PdpRespHeroCarousel.counterMedia"
            defaultMessage="{position, number} of {totalItems, number}"
            values={{ position: index + 1, totalItems }}
        />
    ) : (
        <FormattedMessage
            id="PdpRespHeroCarousel.counter"
            defaultMessage="{position, number} of {totalItems, number} images"
            values={{ position: index + 1, totalItems }}
        />
    );

const MAX_THUMBS_PER_PAGE = 6;
const MEDIA_TYPE_VIEW_IN_ROOM = 'viewInRoom';
const MEDIA_TYPE_VIDEO = 'VIMEO';

export class PdpRespHeroCarouselComponent extends React.Component {
    constructor(props) {
        super(props);

        this.onRoomClick = this.onRoomClick.bind(this);
        this.onImageClick = this.onImageClick.bind(this);
        this.onIndexChange = this.onIndexChange.bind(this);
        this.onModalCloseClick = this.onModalCloseClick.bind(this);
        this.renderCarouselItem = this.renderCarouselItem.bind(this);
        this.renderCarouselThumb = this.renderCarouselThumb.bind(this);
        this.renderCarouselThumbsList = this.renderCarouselThumbsList.bind(this);

        this.state = {
            index: 0,
            isJumpToItemForced: false,
            modalOpen: false,
        };
        this.setInstanceProperties(props, true);
    }

    componentWillReceiveProps(nextProps) {
        this.setInstanceProperties(nextProps);
    }

    setInstanceProperties(props, disableStatefulOperations) {
        const { item = {}, viewer, user, defaultSkuId, selectedSkuId } = props;
        const { viewInRoomPhotos, isMultiSku } = item;
        const activeSkuId = selectedSkuId || defaultSkuId;
        const prevPhotos = this._photos || get(item, 'sortedPhotos');
        this._photos = isMultiSku ? getSortedPhotos(activeSkuId, prevPhotos) : item.photos || [];
        const firstPhotoChanged =
            !!prevPhotos &&
            !!this._photos &&
            get(prevPhotos, '0.serviceId') !== get(this._photos, '0.serviceId');
        if (firstPhotoChanged && !disableStatefulOperations) {
            this.resetThumbCarousel();
        }

        const carouselPhotos = this._photos.filter(({ largePath }) => largePath);
        this._mediaZoom = carouselPhotos.map(({ masterOrZoomPath: src }) => ({
            src,
        }));
        this._roomViewItem = getRoomViewItem({ item, user });
        this._media = [
            ...carouselPhotos.map(({ largePath: src, alt }) => ({ src, alt })),
            ...viewInRoomPhotos.map((obj, index) => ({
                ...obj,
                roomIndex: index,
                type: MEDIA_TYPE_VIEW_IN_ROOM,
            })),
        ];
        this._mediaThumbs = [
            ...carouselPhotos.map(({ largePath, masterOrZoomPath, alt } = {}) => ({
                src: largePath,
                srcSet: getThumbsSrcSetString({ url: masterOrZoomPath }),
                alt,
            })),
            ...viewInRoomPhotos.map(({ src, srcSetProps, credits }) => ({
                src,
                alt: credits,
                srcSet: get(srcSetProps, 'srcSet'),
                type: MEDIA_TYPE_VIEW_IN_ROOM,
            })),
        ];
        // add video only if the video-on-PDP feature is enabled and the item is jewelry
        this._videoVimeo =
            get(viewer, 'videoPdpEnabled') && get(item, 'isJewelry') && get(item, 'videoVimeo');
        if (this._videoVimeo && this._videoVimeo.status === 'available') {
            const video = {
                src: get(this._videoVimeo, 'thumbnail'),
                type: MEDIA_TYPE_VIDEO,
            };
            // default insert as second item
            let targetVimeoIndex = 1;
            if (isMultiSku) {
                // get first product image index and add video before it
                targetVimeoIndex = carouselPhotos.findIndex(
                    ({ imageSkuXrefs }) => !imageSkuXrefs.length
                );
            }
            this._media.splice(targetVimeoIndex, 0, video);
            const id = get(this._videoVimeo, 'vimeoId');
            this._mediaZoom.splice(targetVimeoIndex, 0, {
                ...video,
                vimeoPlayerOptions: {
                    quality: '4k',
                    id,
                    tracking: videoTracking,
                },
            });
            this._mediaThumbs.splice(targetVimeoIndex, 0, video);
        }
        this._totalItems = get(viewer, 'isRequestMoreImagesEnabled')
            ? this._media.length + 1
            : this._media.length;
    }

    /**
     * Handler for when close 'x' is clicked (or ESC is pressed)
     */
    onModalCloseClick({ index }) {
        this.setState({ index, isJumpToItemForced: true, modalOpen: false });
    }
    /**
     * Handler for when image is clicked
     *
     * @param {number} index - clicked index
     */
    onImageClick({ index }) {
        this.setState({ modalOpen: true }, () => {
            // fire tracking
            trackClickCarousel({
                index,
                total: this._totalItems,
            });
        });
    }
    /**
     * Handler for when room is clicked
     */
    onRoomClick({ index }) {
        this.setState({ modalOpen: true }, () => {
            trackViewRoomClick({
                index,
                total: this._totalItems,
            });
        });
    }
    /**
     * Handler for when image is changed
     *
     * @param {number} index - new index
     * @param {string} change - either 'previous' or 'next'
     */
    onIndexChange({ change, index }) {
        const {
            item: { viewInRoomPhotos },
        } = this.props;
        const total = this._totalItems;

        this.setState({ index, isJumpToItemForced: false }, () => {
            trackChangeCarousel({
                index,
                total,
                next: change === 'next',
            });
            if (index >= this._media.length - viewInRoomPhotos.length && !!this._media[index]) {
                trackViewRoomDisplayed({ index, total });
            }
        });
    }
    renderCarouselItem({ index, isCurrentItem }) {
        const position = index + 1;
        const {
            item,
            user,
            disableZoomModal,
            disableRequestMoreImagesModal,
            isCondensedCarousel,
            viewer,
        } = this.props;
        const { viewInRoomPhotos } = item;
        const { src, alt, type, roomIndex } = this._media[index] || {};
        const isRequestMoreImagesEnabled = get(viewer, 'isRequestMoreImagesEnabled');
        const isCarouselLastItem = position === this._totalItems;
        const isRequestMoreImages = isCarouselLastItem && isRequestMoreImagesEnabled;
        const isShowViewInRoom = type === MEDIA_TYPE_VIEW_IN_ROOM;
        const isVideo = type === MEDIA_TYPE_VIDEO;
        const wrapperProps = {
            className: classnames(styles.item, {
                [styles.moreImages]: isRequestMoreImages || isShowViewInRoom,
                'is-zoom': !isRequestMoreImages,
                [styles.condensedCarousel]: isCondensedCarousel,
            }),
            onClick:
                isRequestMoreImages || isShowViewInRoom || disableZoomModal
                    ? () => ({})
                    : () => this.onImageClick({ index }),
        };

        const img = (
            <img className={styles.photo} src={src} alt={alt} data-tn="pdp-hero-carousel-image" />
        );

        // Don't want to lazy load 1st image
        let mediaElement =
            index === 0 ? (
                img
            ) : (
                <Fragment>
                    <LazyLoad
                        className={styles.lazyLoad}
                        offsetHorizontal={HORIZONTAL_OFFSET_RESP_IMG}
                        key={index}
                    >
                        {img}
                    </LazyLoad>
                    <noscript
                        dangerouslySetInnerHTML={{
                            __html: `<img src="${src}" alt="${alt}" />`,
                        }}
                    />
                </Fragment>
            );

        if (isShowViewInRoom) {
            mediaElement = (
                <LazyLoad
                    key={index}
                    className={styles.lazyLoad}
                    offsetHorizontal={HORIZONTAL_OFFSET_RESP_IMG}
                >
                    <PdpRespHeroCarouselViewInRoom
                        roomViewItem={this._roomViewItem}
                        room={viewInRoomPhotos[roomIndex]}
                        onClick={() => this.onRoomClick({ index })}
                    />
                </LazyLoad>
            );
        } else if (isVideo) {
            mediaElement = (
                <PdpRespHeroCarouselVimeoLazyLoader
                    isCurrentItem={isCurrentItem}
                    id={get(this._videoVimeo, 'vimeoId')}
                />
            );
        } else if (isRequestMoreImages) {
            mediaElement = (
                <PdpRespHeroCarouselRequestMoreImages
                    disableRequestMoreImagesModal={disableRequestMoreImagesModal}
                    item={item}
                    user={user}
                    viewer={viewer}
                />
            );
        }

        return (
            <div {...wrapperProps} data-tn={`pdp-image-carousel-image-${position}`}>
                {mediaElement}
            </div>
        );
    }

    renderCarouselThumb({ index, isCurrentItem }) {
        const {
            relayVariables: { isClient },
        } = this.props;
        const thumbSrc = get(this, `_mediaThumbs[${index}].src}`);
        const { type } = this._media[index] || {};
        const isRoomView = type === MEDIA_TYPE_VIEW_IN_ROOM;
        const isVideo = type === MEDIA_TYPE_VIDEO;
        return (
            <PdpSharedHeroCarouselThumb
                key={`${thumbSrc}-${index}`}
                index={index}
                isClient={isClient}
                thumbs={this._mediaThumbs}
                totalItems={this._totalItems}
                onIndexChange={() => this.onIndexChange({ index })}
                isCurrentItem={isCurrentItem}
                isRequestMoreImagesEnabled={get(this.props.viewer, 'isRequestMoreImagesEnabled')}
                isRoomView={isRoomView}
                isVideo={isVideo}
            />
        );
    }

    resetThumbCarousel() {
        this.setState({ index: 0, isJumpToItemForced: true });
    }

    renderCarouselThumbsList({ index }) {
        const items = [];
        for (let i = 0; i < this._totalItems; i++) {
            items.push(this.renderCarouselThumb({ index: i, isCurrentItem: i === index }));
        }
        return items;
    }

    render() {
        const {
            itemTitle,
            itemImage,
            environment,
            relayVariables,
            hideArrows,
            showDots,
            showCounter,
            item,
        } = this.props;
        const { viewInRoomPhotos, address } = item;
        const { index, modalOpen } = this.state;
        const itemId = relayVariables ? relayVariables.itemId : '';
        const renderDot = showDots
            ? ({ isCurrentDot = false } = {}) => <PdpSharedCarouselDot isActive={isCurrentDot} />
            : null;
        const showThumbs = !this.props.isCondensedCarousel;
        const showGalleryTag = get(address, 'isGalleryRoomAddress') || false;

        return (
            <div className={`${styles.wrapper} enable-click-on-preview`}>
                {/* Trade On-Approval Callout */}
                <SharedNotificationsWrapper
                    type={SHARED_NOTIFICATION_KEYS.PDP_TRADE_ON_APPROVAL_CALLOUT}
                >
                    {({ isEnabled }) => {
                        if (isEnabled && !showGalleryTag) {
                            return (
                                <React.Suspense fallback="">
                                    <PdpRespHeroCarouselTradeOnApprovalCalloutLazy
                                        itemId={itemId}
                                        relayEnvironment={environment}
                                    />
                                </React.Suspense>
                            );
                        }
                        return null;
                    }}
                </SharedNotificationsWrapper>
                {/* Zoom Modal */}
                <PdpRespZoomModalLoader
                    environment={environment}
                    relayVariables={relayVariables}
                    isOpen={modalOpen}
                    totalItems={this._totalItems}
                    onCloseClick={this.onModalCloseClick}
                    startingIndex={index}
                    zoomMedia={this._mediaZoom}
                    roomViewItem={this._roomViewItem}
                    rooms={viewInRoomPhotos}
                    itemTitle={itemTitle}
                    itemImage={itemImage}
                    itemId={itemId}
                />
                {/* Images */}
                <Carousel
                    itemKey="hero-item"
                    dataTn="pdp-image-carousel"
                    index={index}
                    totalItems={this._totalItems}
                    enableKeybordControl={!modalOpen} // disable arrows if modal is open
                    onIndexChange={this.onIndexChange}
                    renderItem={this.renderCarouselItem}
                    hideArrows={hideArrows}
                    showDots={showDots}
                    renderDot={renderDot}
                />
                {/* Counter */}
                {showCounter && (
                    <span className={styles.counter} data-tn="pdp-image-carousel-count-text">
                        {counter(
                            index,
                            this._totalItems,
                            get(this.props.viewer, 'videoPdpEnabled')
                        )}
                    </span>
                )}
                {/* Thumbs */}
                {showThumbs && (
                    <div className={styles.thumbs}>
                        {this._totalItems > MAX_THUMBS_PER_PAGE ? (
                            <Carousel
                                itemKey="hero-item-thumb"
                                dataTn="pdp-image-carousel-thumbs"
                                index={index}
                                totalItems={this._totalItems}
                                itemsPerPage={MAX_THUMBS_PER_PAGE}
                                slideCarouselItems
                                enableItemControl
                                hideArrows
                                isJumpToItemForced={this.state.isJumpToItemForced}
                                onIndexChange={this.onIndexChange}
                                renderItem={this.renderCarouselThumb}
                            />
                        ) : (
                            <div className={styles.thumbsList}>
                                {this.renderCarouselThumbsList({ index })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }
}

PdpRespHeroCarouselComponent.defaultProps = {
    disableZoomModal: false,
    disableRequestMoreImagesModal: false,
    isCondensedCarousel: false,
    hideArrows: false,
    showDots: false,
    showCounter: true,
};

PdpRespHeroCarouselComponent.propTypes = {
    item: PropTypes.object.isRequired,
    user: PropTypes.object,
    relayVariables: PropTypes.shape({
        userId: PropTypes.string,
        itemId: PropTypes.string.isRequired,
        fetchUser: PropTypes.bool.isRequired,
        isClient: PropTypes.bool,
    }).isRequired,
    environment: PropTypes.object.isRequired,
    itemImage: PropTypes.string,
    itemTitle: PropTypes.string,
    viewer: PropTypes.object,
    disableZoomModal: PropTypes.bool.isRequired,
    disableRequestMoreImagesModal: PropTypes.bool.isRequired,
    isCondensedCarousel: PropTypes.bool.isRequired,
    hideArrows: PropTypes.bool.isRequired,
    showDots: PropTypes.bool.isRequired,
    selectedSkuId: PropTypes.string,
    showCounter: PropTypes.bool.isRequired,
};

export const PdpRespHeroCarousel = createFragmentContainer(PdpRespHeroCarouselComponent, {
    item: graphql`
        fragment PdpRespHeroCarousel_item on Item {
            ...PdpRespHeroCarouselRequestMoreImages_item
            isJewelry
            defaultSkuId
            isMultiSku
            photos {
                largePath
                masterOrZoomPath
                alt
            }
            viewInRoomPhotos {
                src
                credits
                srcSetProps {
                    sizes
                    srcSet
                }
                centerPosition {
                    top
                    left
                }
                comparatorWidth {
                    pixel
                    inches
                }
            }
            measurement {
                convertedMeasurements(unit: IN) {
                    width
                    height
                }
                hoverDimensionsWidth: display(addLabel: false, fields: [width]) {
                    unit
                    value
                }
                hoverDimensionsHeight: display(addLabel: false, fields: [height]) {
                    unit
                    value
                }
            }
            videoVimeo {
                thumbnail(size: w100h75, includePlayButton: false, pad: false)
                status
                vimeoId
            }
            sortedPhotos {
                serviceId
                largePath
                masterOrZoomPath
                alt
                sortOrder
                imageSkuXrefs {
                    skuId
                }
            }
            address {
                isGalleryRoomAddress
            }
        }
    `,
    user: graphql`
        fragment PdpRespHeroCarousel_user on User {
            ...PdpRespHeroCarouselRequestMoreImages_user
            profile {
                measurementUnit
            }
        }
    `,
    viewer: graphql`
        fragment PdpRespHeroCarousel_viewer on Viewer {
            ...PdpRespHeroCarouselRequestMoreImages_viewer
            isRequestMoreImagesEnabled: isEnabledByLocale(featureName: "REQUEST_MORE_IMAGES")
            videoPdpEnabled: isEnabled(key: "videoPdpEnabled")
        }
    `,
});
